import { useMemo } from "react";
import { useAuthStore } from "../stores/authStore";
import { orgKey } from "../lib/orgKey";
import { shopifyAdminAppUrl } from "../lib/shopifyAdmin";

export interface ShopifyBillingGate {
    /** The org must bill through Shopify — hide every Stripe affordance. */
    isShopifyManaged: boolean;
    /**
     * The rail is known *and* it is Stripe. Gate plan grids, portal buttons and
     * card forms on this rather than on `!isShopifyManaged`: an unresolved org
     * must not be read as "Stripe customer".
     */
    stripeRailAllowed: boolean;
    /** False while the org — and therefore the rail — is still unknown. */
    isRailResolved: boolean;
    shopifyShopDomain: string | null;
    /** Deep link to the embedded app's billing page, when we know the shop. */
    shopifyBillingUrl: string | null;
}

/** Mirrors `SHOP_DOMAIN_REGEX` in backend/src/utils/validation.ts. */
const SHOP_DOMAIN_REGEX = /\.myshopify\.com$/i;

const isShopDomain = (value?: string | null): boolean => SHOP_DOMAIN_REGEX.test(String(value || "").trim());

/**
 * Single source of truth for Shopify App Store rule 1.2.1: a Shopify merchant
 * must never be offered an off-platform charge.
 *
 * The rule is enforced fail-closed. Every surface used to compute its own
 * `Boolean(organization?.isShopifyManaged)`, which reads `false` in three
 * situations that have nothing to do with the merchant's actual billing rail:
 * the org lookup missing, `me` not having landed yet, and the Shopify SSO
 * payload not carrying the field at all. Any of those rendered the Stripe card
 * modal to a Shopify merchant. Here an unknown rail blocks both rails instead,
 * and a session minted by Shopify SSO is treated as Shopify-managed regardless
 * of whether the org resolved.
 *
 * A `*.myshopify.com` org domain is treated as the Shopify rail on its own,
 * matching `shopifyBillingRequired` on the server. `isShopifyManaged` only
 * answers "is there an active ShopifyMerchant row", which is false for a
 * merchant who signed up by email and typed their shop host, for an install
 * whose `registerMerchant` call never landed, and for anyone post-uninstall or
 * post-GDPR-redact — all of them Shopify stores, all of them previously shown
 * the plan grid.
 */
export function useShopifyBillingGate(): ShopifyBillingGate {
    const { user, currentOrganization, shopifySession } = useAuthStore();

    return useMemo(() => {
        const organizations: any[] = (user?.organizations as any[]) ?? [];
        const organization =
            organizations.find((org) => orgKey(org) === currentOrganization) ?? null;

        // `undefined` means the payload never carried the field (REST SSO
        // exchange, a stale persisted user); only an explicit boolean counts as
        // knowing the rail. The domain check needs no such caution — a shop host
        // is positive proof, never a reason to open the Stripe rail.
        const orgRail = organization?.shopifyBillingRequired ?? organization?.isShopifyManaged;
        const shopDomainOrg = isShopDomain(organization?.domain) || isShopDomain(organization?.shopifyShopDomain);
        const isShopifyManaged = Boolean(shopifySession) || shopDomainOrg || orgRail === true;
        const isRailResolved = isShopifyManaged || typeof orgRail === "boolean";

        const shopifyShopDomain =
            organization?.shopifyShopDomain || shopifySession?.shopDomain || null;

        return {
            isShopifyManaged,
            stripeRailAllowed: isRailResolved && !isShopifyManaged,
            isRailResolved,
            shopifyShopDomain,
            shopifyBillingUrl: shopifyAdminAppUrl(shopifyShopDomain, "/app/billing"),
        };
    }, [user, currentOrganization, shopifySession]);
}
